<template>
    <div class="conversation-json-manager">
      <!-- Upload Section -->
      <div class="upload-section">
        <div class="section-header">
          <h3>Upload Conversation JSON</h3>
          <p>Replace the entire conversation with a JSON file or direct input</p>
        </div>

        <!-- File Upload -->
        <div class="upload-method" v-if="uploadMethod === 'file'">
          <div class="file-drop-zone"
               :class="{ 'drag-over': isDragOver, 'has-file': selectedFile }"
               @dragover.prevent="isDragOver = true"
               @dragleave.prevent="isDragOver = false"
               @drop.prevent="handleFileDrop">

            <div v-if="!selectedFile" class="drop-content">
              <i class="icon-upload"></i>
              <p>Drag & drop your JSON file here</p>
              <span>or</span>
              <button type="button" class="browse-btn" @click="$refs.fileInput.click()">
                Browse Files
              </button>
            </div>

            <div v-else class="file-selected">
              <i class="icon-file"></i>
              <span class="filename">{{ selectedFile.name }}</span>
              <button type="button" class="remove-file" @click="removeFile">×</button>
            </div>

            <input
              type="file"
              ref="fileInput"
              accept=".json"
              style="display: none"
              @change="handleFileSelect">
          </div>
        </div>

        <!-- Direct JSON Input -->
        <div class="upload-method" v-if="uploadMethod === 'text'">
          <div class="json-editor">
            <textarea
              v-model="jsonInput"
              placeholder="Paste your conversation JSON here..."
              class="json-textarea"
              rows="15">
            </textarea>

            <div class="editor-actions">
              <button type="button" @click="formatJson" class="format-btn">
                Format JSON
              </button>
              <button type="button" @click="validateJson" class="validate-btn">
                Validate
              </button>
            </div>
          </div>
        </div>

        <!-- Method Toggle -->
        <div class="method-toggle">
          <button
            :class="{ active: uploadMethod === 'file' }"
            @click="uploadMethod = 'file'">
            File Upload
          </button>
          <button
            :class="{ active: uploadMethod === 'text' }"
            @click="uploadMethod = 'text'">
            Direct Input
          </button>
        </div>

        <!-- Upload Options -->
        <div class="upload-options">
          <label class="checkbox-option">
            <input type="checkbox" v-model="validateOnly">
            <span>Validate only (don't save)</span>
          </label>
          <label class="checkbox-option">
            <input type="checkbox" v-model="autoFix">
            <span>Auto-fix common issues</span>
          </label>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button
            @click="uploadConversation"
            :disabled="!canUpload || isUploading"
            class="upload-btn">
            <span v-if="isUploading" class="loading-spinner"></span>
            {{ isUploading ? 'Processing...' : (validateOnly ? 'Validate' : 'Upload & Replace') }}
          </button>

          <button @click="exportConversation" class="export-btn">
            Export Current
          </button>

          <button @click="resetForm" class="reset-btn">
            Reset
          </button>
        </div>
      </div>

      <!-- Validation Results -->
      <div v-if="validationResults" class="validation-results">
        <div class="section-header">
          <h3>Validation Results</h3>
        </div>

        <div class="validation-summary" :class="validationResults.valid ? 'valid' : 'invalid'">
          <div class="summary-item">
            <span class="label">Status:</span>
            <span class="value" :class="validationResults.valid ? 'success' : 'error'">
              {{ validationResults.valid ? 'Valid' : 'Invalid' }}
            </span>
          </div>

          <div class="summary-item">
            <span class="label">Segments:</span>
            <span class="value">{{ validationResults.segment_count }}</span>
          </div>

          <div v-if="validationResults.auto_fixed" class="summary-item">
            <span class="label">Auto-fixed:</span>
            <span class="value success">Yes</span>
          </div>
        </div>

        <!-- Errors -->
        <div v-if="validationResults.errors && validationResults.errors.length > 0" class="validation-errors">
          <h4>Errors Found:</h4>
          <ul>
            <li v-for="error in validationResults.errors" :key="error" class="error-item">
              {{ error }}
            </li>
          </ul>
        </div>

        <!-- Preview -->
        <div v-if="validationResults.preview" class="preview-section">
          <h4>Preview (First 3 Segments):</h4>
          <div class="preview-segments">
            <div v-for="(segment, index) in validationResults.preview" :key="index" class="preview-segment">
              <div class="segment-header">
                <span class="speaker">{{ segment.speaker }}</span>
                <span class="role">({{ segment.role }})</span>
                <span v-if="segment.section" class="section">{{ segment.section }}</span>
              </div>
              <div class="segment-content">{{ segment.content }}</div>
            </div>
          </div>
        </div>

        <!-- Success Actions -->
        <div v-if="validationResults.valid && !validateOnly" class="success-actions">
          <button @click="proceedWithUpload" class="proceed-btn">
            Confirm & Apply Changes
          </button>
        </div>
      </div>

      <!-- Success Message -->
      <div v-if="uploadSuccess" class="success-message">
        <i class="icon-check"></i>
        <span>Conversation updated successfully!</span>
        <button @click="uploadSuccess = false" class="close-success">×</button>
      </div>
    </div>
  </template>

  <script>
  import API from '@/api';

  export default {
    name: 'ConversationJsonManager',
    props: {
      messageId: {
        required: true
      }
    },
    data() {
      return {
        uploadMethod: 'file', // 'file' or 'text'
        selectedFile: null,
        jsonInput: '',
        isDragOver: false,
        validateOnly: true, // Default to validate first
        autoFix: true,
        isUploading: false,
        validationResults: null,
        uploadSuccess: false,
      };
    },
    computed: {
      canUpload() {
        if (this.uploadMethod === 'file') {
          return this.selectedFile !== null;
        } else {
          return this.jsonInput.trim().length > 0;
        }
      }
    },
    methods: {
      handleFileDrop(e) {
        this.isDragOver = false;
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          this.handleFile(files[0]);
        }
      },

      handleFileSelect(e) {
        const files = e.target.files;
        if (files.length > 0) {
          this.handleFile(files[0]);
        }
      },

      handleFile(file) {
        if (!file.name.endsWith('.json')) {
          this.$emit('warning', { message: 'Please select a JSON file', type: 'warning' });
          return;
        }

        this.selectedFile = file;

        // Also read the file content for text editing if needed
        const reader = new FileReader();
        reader.onload = (e) => {
          this.jsonInput = e.target.result;
        };
        reader.readAsText(file);
      },

      removeFile() {
        this.selectedFile = null;
        this.jsonInput = '';
        this.$refs.fileInput.value = '';
      },

      formatJson() {
        try {
          const parsed = JSON.parse(this.jsonInput);
          this.jsonInput = JSON.stringify(parsed, null, 2);
        } catch (e) {
          this.$emit('warning', { message: 'Invalid JSON format', type: 'error' });
        }
      },

      validateJson() {
        try {
          JSON.parse(this.jsonInput);
          this.$emit('warning', { message: 'JSON syntax is valid', type: 'success' });
        } catch (e) {
          this.$emit('warning', { message: `JSON syntax error: ${e.message}`, type: 'error' });
        }
      },

      async uploadConversation() {
        this.isUploading = true;
        this.validationResults = null;

        try {
          let response;

          if (this.uploadMethod === 'file' && this.selectedFile) {
            // File upload method
            const formData = new FormData();
            formData.append('file', this.selectedFile);
            formData.append('validate_only', this.validateOnly);
            formData.append('auto_fix', this.autoFix);

            response = await API.post(
              `/user_customization/messages/${this.messageId}/conversation/upload`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          } else {
            // JSON payload method
            let conversationData;
            try {
              const parsed = JSON.parse(this.jsonInput);
              conversationData = Array.isArray(parsed) ? parsed : parsed.conversation || [parsed];
            } catch (e) {
              throw new Error(`Invalid JSON: ${e.message}`);
            }

            response = await API.post(
              `/user_customization/messages/${this.messageId}/conversation/update`,
              {
                conversation: conversationData,
                validate_only: this.validateOnly
              }
            );
          }

          this.validationResults = response.data;

          if (response.data.success && !this.validateOnly) {
            this.uploadSuccess = true;
            this.$emit('conversation-updated', response.data.updated_response);

            // Reset form after successful upload
            setTimeout(() => {
              this.resetForm();
            }, 2000);
          }

        } catch (error) {
          console.error('Upload error:', error);
          this.$emit('warning', {
            message: error.response?.data?.detail || error.message || 'Upload failed',
            type: 'error'
          });
        } finally {
          this.isUploading = false;
        }
      },

      async proceedWithUpload() {
        // If validation was successful, now do the actual upload
        this.validateOnly = false;
        await this.uploadConversation();
        this.validateOnly = true; // Reset for next time
      },

      async exportConversation() {
        try {
          const response = await API.get(
            `/user_customization/messages/${this.messageId}/conversation/export`,
            {
              params: { format_type: 'conversation_only' },
              responseType: 'blob'
            }
          );

          // Create download link
          const blob = new Blob([response.data], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `conversation_${this.messageId}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.$emit('warning', { message: 'Conversation exported successfully', type: 'success' });

        } catch (error) {
          console.error('Export error:', error);
          this.$emit('warning', { message: 'Export failed', type: 'error' });
        }
      },

      resetForm() {
        this.selectedFile = null;
        this.jsonInput = '';
        this.validationResults = null;
        this.uploadSuccess = false;
        this.validateOnly = true;
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      }
    }
  };
  </script>

  <style scoped>
  .conversation-json-manager {
    max-width: 800px;
    margin: 0 auto;
    padding: 1rem;
  }

  .section-header {
    margin-bottom: 1rem;
  }

  .section-header h3 {
    margin: 0 0 0.5rem 0;
    color: #2d3748;
    font-size: 1.25rem;
  }

  .section-header p {
    margin: 0;
    color: #718096;
    font-size: 0.875rem;
  }

  .upload-section {
    background: #f7fafc;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .file-drop-zone {
    border: 2px dashed #cbd5e0;
    border-radius: 8px;
    padding: 2rem;
    text-align: center;
    transition: all 0.2s ease;
    background: white;
  }

  .file-drop-zone.drag-over {
    border-color: #3182ce;
    background-color: #ebf8ff;
  }

  .file-drop-zone.has-file {
    border-color: #38a169;
    background-color: #f0fff4;
  }

  .drop-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .icon-upload::before {
    content: "📁";
    font-size: 2rem;
  }

  .browse-btn {
    background: #3182ce;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }

  .browse-btn:hover {
    background: #2c5282;
  }

  .file-selected {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .icon-file::before {
    content: "📄";
  }

  .filename {
    font-weight: 500;
    color: #2d3748;
  }

  .remove-file {
    background: #e53e3e;
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    font-size: 1rem;
    line-height: 1;
  }

  .json-editor {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
  }

  .json-textarea {
    width: 100%;
    border: none;
    padding: 1rem;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.875rem;
    resize: vertical;
    outline: none;
  }

  .editor-actions {
    padding: 0.5rem 1rem;
    background: #f7fafc;
    border-top: 1px solid #e2e8f0;
    display: flex;
    gap: 0.5rem;
  }

  .format-btn, .validate-btn {
    padding: 0.25rem 0.5rem;
    border: 1px solid #cbd5e0;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
  }

  .format-btn:hover, .validate-btn:hover {
    background: #edf2f7;
  }

  .method-toggle {
    display: flex;
    gap: 0.5rem;
    margin: 1rem 0;
  }

  .method-toggle button {
    padding: 0.5rem 1rem;
    border: 1px solid #cbd5e0;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .method-toggle button.active {
    background: #3182ce;
    color: white;
    border-color: #3182ce;
  }

  .upload-options {
    display: flex;
    gap: 1rem;
    margin: 1rem 0;
  }

  .checkbox-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #4a5568;
  }

  .checkbox-option input[type="checkbox"] {
    margin: 0;
  }

  .action-buttons {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }

  .upload-btn, .export-btn, .reset-btn, .proceed-btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .upload-btn {
    background: #3182ce;
    color: white;
  }

  .upload-btn:hover:not(:disabled) {
    background: #2c5282;
  }

  .upload-btn:disabled {
    background: #a0aec0;
    cursor: not-allowed;
  }

  .export-btn {
    background: #38a169;
    color: white;
  }

  .export-btn:hover {
    background: #2f855a;
  }

  .reset-btn {
    background: #718096;
    color: white;
  }

  .reset-btn:hover {
    background: #4a5568;
  }

  .proceed-btn {
    background: #d69e2e;
    color: white;
  }

  .proceed-btn:hover {
    background: #b7791f;
  }

  .loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .validation-results {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .validation-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    padding: 1rem;
    border-radius: 6px;
    margin-bottom: 1rem;
  }

  .validation-summary.valid {
    background: #f0fff4;
    border: 1px solid #9ae6b4;
  }

  .validation-summary.invalid {
    background: #fed7d7;
    border: 1px solid #fc8181;
  }

  .summary-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .summary-item .label {
    font-weight: 500;
    color: #4a5568;
  }

  .summary-item .value.success {
    color: #38a169;
    font-weight: 600;
  }

  .summary-item .value.error {
    color: #e53e3e;
    font-weight: 600;
  }

  .validation-errors {
    margin: 1rem 0;
  }

  .validation-errors h4 {
    color: #e53e3e;
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
  }

  .validation-errors ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  .error-item {
    color: #c53030;
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
  }

  .preview-section {
    margin: 1.5rem 0;
  }

  .preview-section h4 {
    margin: 0 0 1rem 0;
    color: #2d3748;
    font-size: 1rem;
  }

  .preview-segments {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .preview-segment {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 1rem;
  }

  .segment-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }

  .speaker {
    font-weight: 600;
    color: #3182ce;
  }

  .role {
    color: #718096;
    font-style: italic;
  }

  .section {
    background: #edf2f7;
    color: #4a5568;
    padding: 0.125rem 0.375rem;
    border-radius: 3px;
    font-size: 0.75rem;
  }

  .segment-content {
    color: #2d3748;
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .success-actions {
    margin-top: 1.5rem;
    padding: 1rem;
    background: #f0fff4;
    border: 1px solid #9ae6b4;
    border-radius: 6px;
    text-align: center;
  }

  .success-message {
    background: #f0fff4;
    border: 1px solid #9ae6b4;
    color: #22543d;
    padding: 1rem;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    position: relative;
  }

  .icon-check::before {
    content: "✓";
    color: #38a169;
    font-weight: bold;
  }

  .close-success {
    background: none;
    border: none;
    color: #22543d;
    cursor: pointer;
    font-size: 1.25rem;
    line-height: 1;
    margin-left: auto;
    padding: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .close-success:hover {
    background: rgba(56, 161, 105, 0.1);
  }

  /* Dark theme support */
  .theme--dark .conversation-json-manager {
    color: #e2e8f0;
  }

  .theme--dark .section-header h3 {
    color: #e2e8f0;
  }

  .theme--dark .section-header p {
    color: #a0aec0;
  }

  .theme--dark .upload-section {
    background: #1a202c;
  }

  .theme--dark .file-drop-zone {
    background: #2d3748;
    border-color: #4a5568;
  }

  .theme--dark .json-editor {
    background: #2d3748;
    border-color: #4a5568;
  }

  .theme--dark .json-textarea {
    background: #2d3748;
    color: #e2e8f0;
  }

  .theme--dark .editor-actions {
    background: #1a202c;
    border-color: #4a5568;
  }

  .theme--dark .format-btn,
  .theme--dark .validate-btn,
  .theme--dark .method-toggle button {
    background: #2d3748;
    border-color: #4a5568;
    color: #e2e8f0;
  }

  .theme--dark .format-btn:hover,
  .theme--dark .validate-btn:hover,
  .theme--dark .method-toggle button:hover {
    background: #4a5568;
  }

  .theme--dark .validation-results {
    background: #1a202c;
    border-color: #4a5568;
  }

  .theme--dark .preview-segment {
    background: #2d3748;
    border-color: #4a5568;
  }

  .theme--dark .segment-content {
    color: #e2e8f0;
  }

  .theme--dark .summary-item .label {
    color: #a0aec0;
  }

  .theme--dark .checkbox-option {
    color: #a0aec0;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .conversation-json-manager {
      padding: 0.5rem;
    }

    .upload-section {
      padding: 1rem;
    }

    .action-buttons {
      flex-direction: column;
    }

    .upload-btn, .export-btn, .reset-btn {
      width: 100%;
      justify-content: center;
    }

    .validation-summary {
      grid-template-columns: 1fr;
    }

    .method-toggle {
      flex-direction: column;
    }

    .upload-options {
      flex-direction: column;
      gap: 0.5rem;
    }
  }

  </style>
